import React, { useCallback, useEffect, useRef } from 'react';
import PlaylistItem from '../../models/playlistItem/playlistItem';
import Source from '../../models/source';
import { NowPlaying, saveNowPlaying, usesAudioElement } from '../../services/playback/nowPlaying';

/**
 * How often the playhead reaches localStorage. `timeupdate` fires four times
 * a second and Mixcloud's progress event rather more, so the writes are
 * throttled; the cost of the throttle is at most this much lost on a crash,
 * and a reload or a pause flushes immediately.
 */
const writeIntervalMs = 4000;

/** How often the embeds are asked where they are, having no event to give. */
const pollIntervalMs = 4000;

interface Deps {
  currentTrack: PlaylistItem;
  /** Where the track sits in the active playlist. */
  playlistIndex: number;
  audioEl: React.RefObject<HTMLAudioElement | null>;
  ytPlayer: React.MutableRefObject<YT.Player | undefined>;
  /** The Web Playback SDK player, once it has connected. */
  spotifyPlayer?: Spotify.Player;
}

/**
 * Remembers what is playing and how far in, so that a reload comes back to
 * the same place.
 *
 * Each surface reports differently, and none of them the same way:
 *
 * - `<audio>` has `timeupdate`, which is an event and free.
 * - The YouTube IFrame player and the Spotify SDK have to be *asked* -
 *   `getCurrentTime()` and `getCurrentState()` - so those are polled, and
 *   only while their own source is the current track.
 * - Mixcloud's widget does emit progress, but the widget lives in the
 *   wrapper, so that one is pushed in through `recordPosition`.
 *
 * Nothing here starts, stops or seeks anything. Restoring is the player's
 * job (see the `play` effect); this half only writes down where we got to.
 */
export default function useNowPlayingPosition({
  currentTrack,
  playlistIndex,
  audioEl,
  ytPlayer,
  spotifyPlayer,
}: Deps) {
  const trackRef = useRef(currentTrack);
  trackRef.current = currentTrack;

  const indexRef = useRef(playlistIndex);
  indexRef.current = playlistIndex;

  /** The most recent reading, written or not. What a flush writes. */
  const latestRef = useRef<NowPlaying>(undefined);
  const lastWriteAtRef = useRef(0);

  const recordPosition = useCallback(function recordPosition(
    positionMs: number,
    durationMs: number,
    /**
     * The source this reading came from. Given where a surface can outlive
     * its turn - the Mixcloud widget keeps its show while something else
     * plays - so that a stray late event is not filed under whatever is
     * playing now.
     */
    fromSource?: Source,
  ) {
    const item = trackRef.current;

    if (!item || item.trackType === Source.Null) {
      return;
    }

    if (fromSource !== undefined && fromSource !== item.trackType) {
      return;
    }

    // A player that has not loaded anything reports NaN, and a NaN playhead
    // written now is a track that will not resume later.
    if (!isFinite(positionMs) || positionMs < 0) {
      return;
    }

    const previous = latestRef.current;

    latestRef.current = {
      trackId: item.id,
      trackType: item.trackType,
      index: indexRef.current,
      positionMs,
      durationMs: isFinite(durationMs) && durationMs > 0 ? durationMs : 0,
      savedAt: Date.now(),
    };

    const now = Date.now();

    // The first reading for a new track is written at once. Otherwise a
    // reload in the seconds after pressing play would come back to the track
    // before it - the throttle would still be holding the change.
    const isNewTrack =
      !previous || previous.trackId !== item.id || previous.trackType !== item.trackType;

    if (!isNewTrack && now - lastWriteAtRef.current < writeIntervalMs) {
      return;
    }

    lastWriteAtRef.current = now;
    saveNowPlaying(latestRef.current);
  }, []);

  const flush = useCallback(function flush() {
    if (!latestRef.current) {
      return;
    }

    lastWriteAtRef.current = Date.now();
    saveNowPlaying(latestRef.current);
  }, []);

  useEffect(
    function followAudioElement() {
      const el = audioEl.current;

      if (!el) {
        return;
      }

      function report() {
        // The element keeps its src when playback moves to YouTube, Spotify or
        // Mixcloud, and the pause that comes with the handover fires an event
        // here. Writing then would file the old playhead under the new track.
        if (!usesAudioElement(trackRef.current.trackType)) {
          return;
        }

        // Paused at the very start is "nothing has happened yet", not a
        // position - and it is the state a track restored from the last
        // session sits in until the seek lands. Writing it would spend the
        // position on the way to using it.
        if (el!.paused && el!.currentTime === 0) {
          return;
        }

        recordPosition(el!.currentTime * 1000, el!.duration * 1000);
      }

      function reportNow() {
        report();
        flush();
      }

      el.addEventListener('timeupdate', report);
      // The moments most worth being exact about, and the cheapest to catch.
      el.addEventListener('pause', reportNow);
      el.addEventListener('seeked', reportNow);

      return function cleanup() {
        el.removeEventListener('timeupdate', report);
        el.removeEventListener('pause', reportNow);
        el.removeEventListener('seeked', reportNow);
      };
    },
    [audioEl, recordPosition, flush],
  );

  useEffect(
    function pollYouTube() {
      if (currentTrack.trackType !== Source.YouTube) {
        return;
      }

      const interval = setInterval(function askThePlayer() {
        const player = ytPlayer.current;

        // getCurrentTime is absent until the player is ready, and the ref
        // outlives the player it was set from.
        if (!player || typeof player.getCurrentTime !== 'function') {
          return;
        }

        try {
          // The ref outlives the player it was set from: a new YT.Player is
          // built per video, and for a moment after a track change the old one
          // is still here with the old playhead. The url names the video it
          // actually holds.
          if (player.getVideoUrl().indexOf(trackRef.current.id) < 0) {
            return;
          }

          const position = player.getCurrentTime();

          // As above: a cued video that nobody has pressed play on yet has no
          // position worth keeping, and the one it would overwrite is the one
          // it was cued to.
          if (position === 0 && player.getPlayerState() !== YT.PlayerState.PLAYING) {
            return;
          }

          recordPosition(position * 1000, player.getDuration() * 1000, Source.YouTube);
        } catch (err) {
          console.debug('YouTube player would not report its position', err);
        }
      }, pollIntervalMs);

      return function cleanup() {
        clearInterval(interval);
      };
    },
    [currentTrack, ytPlayer, recordPosition],
  );

  useEffect(
    function pollSpotify() {
      if (currentTrack.trackType !== Source.Spotify || !spotifyPlayer) {
        return;
      }

      const interval = setInterval(function askTheSdk() {
        spotifyPlayer.getCurrentState().then(function read(state) {
          if (state && !(state.paused && state.position === 0)) {
            recordPosition(state.position, state.duration, Source.Spotify);
          }
        }, console.debug);
      }, pollIntervalMs);

      return function cleanup() {
        clearInterval(interval);
      };
    },
    [currentTrack, spotifyPlayer, recordPosition],
  );

  useEffect(
    function flushOnTheWayOut() {
      function flushIfHidden() {
        if (document.visibilityState === 'hidden') {
          flush();
        }
      }

      // `pagehide` rather than `beforeunload`, and `visibilitychange` as well,
      // because iOS fires neither of the other two reliably - the same reason
      // docs/MOBILE-IOS.md gives for the app-data queues. The throttled writes
      // above are what make a missed flush cost seconds rather than the track.
      window.addEventListener('pagehide', flush);
      document.addEventListener('visibilitychange', flushIfHidden);

      return function cleanup() {
        window.removeEventListener('pagehide', flush);
        document.removeEventListener('visibilitychange', flushIfHidden);
      };
    },
    [flush],
  );

  return { recordPosition };
}
